/* eslint-disable prefer-const */
import Grid from '@material-ui/core/Grid';
import { useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import groupBy from 'lodash/groupBy';
import map from 'lodash/map';
import round from 'lodash/round';
import sumBy from 'lodash/sumBy';
import { FC, useEffect, useMemo } from 'react';
import {
  DEFAULT_DIAGRAM_COLORS,
  getDiagramUniversColorForRank,
} from '../../../../shared/utils/diagramUnivers';
import MinorResultPieChart from './MinorResultPieChart';
import { useMinorResultPieDiagramStyles } from './MinorResultPieDiagram.styles';

type MinorResultPieDiagramProps = {
  userResponse: any;
  sendDataMax?: (data: any) => void;
  showLegend?: boolean;
  isSimplePie?: boolean;
};

const stripHtml = (html: string) => {
  const tmp = document.createElement('DIV');
  tmp.innerHTML = html || '';
  return tmp.textContent || tmp.innerText || '';
};

const MinorResultPieDiagram: FC<MinorResultPieDiagramProps> = ({
  userResponse,
  sendDataMax,
  showLegend = true,
  isSimplePie = false,
}) => {
  const classes = useMinorResultPieDiagramStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const univers = userResponse?.attributes?.test?.data?.attributes?.univers;

  let allVariables: any[] = [];
  userResponse?.attributes?.responses?.data.forEach((response: any) => {
    response?.attributes.variable.data.forEach((variable: any) => {
      allVariables.push({
        ...variable,
        variableValue: response.attributes.variableValue,
      });
    });
  });

  const variableGroup = groupBy(allVariables, (variable: any) => variable.id);

  const dataMapValue = map(variableGroup, (variables: any[]) => {
    const sumVariable = sumBy(variables, (variable: any) => variable.variableValue);
    const firstVariable = variables[0];

    return {
      value: sumVariable,
      label: stripHtml(firstVariable.attributes.title),
      title: firstVariable.attributes.title,
      image: firstVariable.attributes.image,
      video: firstVariable.attributes.video,
      typeMedia: firstVariable.attributes.typeMedia,
      description: firstVariable.attributes.description,
    };
  });

  const totalValue = sumBy(dataMapValue, (item: any) => item.value) || 0;

  const dataMapValuePourcentage = dataMapValue
    .sort((a: any, b: any) => b.value - a.value)
    .map((item: any, index: number) => {
      const valuePercent = (item.value * 100) / totalValue;
      return {
        ...item,
        value: round(valuePercent, 2),
        color:
          !univers && isSimplePie && dataMapValue.length === 2
            ? DEFAULT_DIAGRAM_COLORS[index === 0 ? 0 : DEFAULT_DIAGRAM_COLORS.length - 1]
            : getDiagramUniversColorForRank(univers, index, 'lightToDark'),
      };
    });

  const maximumResponse = useMemo(() => dataMapValuePourcentage[0], [dataMapValuePourcentage]);

  useEffect(() => {
    if (sendDataMax && maximumResponse) {
      sendDataMax(maximumResponse);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userResponse]);

  const chart = dataMapValuePourcentage && <MinorResultPieChart data={dataMapValuePourcentage} />;

  if (!showLegend) {
    return <div style={!isMobile ? { width: 206, height: 206 } : { width: '100%' }}>{chart}</div>;
  }

  return (
    <Grid
      alignItems="center"
      container={true}
      style={
        !isMobile
          ? {
              flexWrap: 'nowrap',
              gap: 32,
              alignItems: 'center',
              padding: 0,
              margin: 0,
            }
          : { gap: 24 }
      }>
      <Grid item={true} style={!isMobile ? { flex: 'none', width: 'auto' } : { width: '100%' }}>
        {chart}
      </Grid>
      <Grid
        item={true}
        style={
          !isMobile
            ? {
                alignSelf: 'flex-start',
                width: 271,
                flex: 'none',
              }
            : { width: '100%' }
        }>
        <div className={classes.pieLegendList}>
          {dataMapValuePourcentage.map((item: any) => (
            <div key={item.label} className={classes.pieLegendItem}>
              <span className={classes.pieLegendSwatch} style={{ backgroundColor: item.color }} />
              <span className={classes.pieLegendLabel}>{item.label}</span>
            </div>
          ))}
        </div>
      </Grid>
    </Grid>
  );
};

export default MinorResultPieDiagram;
